import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Ticket, XCircle } from "lucide-react";

import { Layout } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isNewOrderZendeskTicketEnabled } from "@/features/newOrderZendeskTicketFeature";
import { canOverrideDynamicsEnvironment } from "@/lib/dynamicsEnvironment";
import { clientsApi, type Client } from "@/lib/api";

// Mesma chave/uso da tela: lista única em cache, atualizada a cada toggle salvo.
const CLIENTS_QUERY_KEY = ["zendesk-ticket-settings-clients"];
// A lista de clientes é grande; só as primeiras linhas do filtro são desenhadas.
const MAX_VISIBLE_ROWS = 100;
const UPDATE_COMMENT = "Alterado na tela de Desenvolvimento (Chamados no Zendesk)";

type StatusFilter = "all" | "enabled";

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string; message?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
    if (response?.data?.message) return response.data.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

const normalize = (value?: string | null) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function ZendeskTicketSettings() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [savingIds, setSavingIds] = useState<number[]>([]);

  const { data: clients = [], isLoading, error } = useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await clientsApi.getAll();
      return (Array.isArray(data) ? data : []).filter((client) => client.clientId != null);
    },
    staleTime: 1000 * 60,
    enabled: canOverrideDynamicsEnvironment && isNewOrderZendeskTicketEnabled,
  });

  const enabledCount = useMemo(() => clients.filter((client) => client.createZendeskTicketOnOrder).length, [clients]);

  const filteredClients = useMemo(() => {
    const term = normalize(search.trim());
    return clients
      .filter((client) => statusFilter === "all" || client.createZendeskTicketOnOrder)
      .filter((client) => !term
        || normalize(client.clientFantasyName).includes(term)
        || normalize(client.clientCompanyName).includes(term)
        || normalize(client.clientCodeCnx).includes(term)
        || normalize(client.clientCnpj).includes(term))
      .sort((first, second) => (first.clientFantasyName ?? "").localeCompare(second.clientFantasyName ?? "", "pt-BR", { sensitivity: "base" }));
  }, [clients, search, statusFilter]);

  if (!canOverrideDynamicsEnvironment || !isNewOrderZendeskTicketEnabled) {
    return <Navigate to="/customer-portal" replace />;
  }

  const handleToggle = async (client: Client, checked: boolean) => {
    const clientId = client.clientId!;
    setSavingIds((current) => [...current, clientId]);
    try {
      // Mesma API da tela de gerenciar cliente (PUT /api/clients/{id}); só o campo do chamado é enviado.
      await clientsApi.update(clientId, { createZendeskTicketOnOrder: checked, comment: UPDATE_COMMENT });
      queryClient.setQueryData<Client[]>(CLIENTS_QUERY_KEY, (current = []) =>
        current.map((item) => (item.clientId === clientId ? { ...item, createZendeskTicketOnOrder: checked } : item)));
      toast.success("Sucesso!", {
        description: `Chamado ao criar pedido ${checked ? "ativado" : "desativado"} para ${client.clientFantasyName ?? client.clientCodeCnx}.`,
      });
    } catch (saveError) {
      console.error("Erro ao salvar chamado no Zendesk ao criar pedido:", saveError);
      toast.error("Erro ao salvar", { description: getApiErrorMessage(saveError, "Não foi possível atualizar o cliente.") });
    } finally {
      setSavingIds((current) => current.filter((id) => id !== clientId));
    }
  };

  const visibleClients = filteredClients.slice(0, MAX_VISIBLE_ROWS);

  return (
    <Layout title="Chamados no Zendesk">
      <div className="space-y-6">
        <Card className="overflow-hidden border-primary/20 shadow-sm">
          <div className="h-1 bg-primary" />
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Ticket className="h-6 w-6 text-primary" />
              Chamados no Zendesk ao criar pedido
            </CardTitle>
            <CardDescription className="max-w-3xl leading-relaxed">
              Clientes com o toggle ativado abrem um chamado no Zendesk quando criam um pedido imediato. A configuração
              fica em client.create_zendesk_ticket_on_order e cada alteração é salva na hora (e registrada no histórico do cliente).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full max-w-xl">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Pesquisar por nome, código ou CNPJ"
                  className="pl-9"
                  disabled={isLoading || !!error}
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  id="only-enabled-filter"
                  checked={statusFilter === "enabled"}
                  onCheckedChange={(checked) => setStatusFilter(checked ? "enabled" : "all")}
                  disabled={isLoading || !!error}
                />
                <label htmlFor="only-enabled-filter" className="cursor-pointer">Somente ativados</label>
              </div>
              {!isLoading && !error && (
                <Badge variant="outline">{enabledCount} de {clients.length} clientes ativados</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {isLoading && (
          <Card><CardContent className="flex items-center gap-3 py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-primary" /> Carregando clientes...
          </CardContent></Card>
        )}

        {error && (
          <Card className="border-destructive/50 bg-destructive/5"><CardContent className="flex items-start gap-3 py-6 text-destructive">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Não foi possível carregar os clientes</p>
              <p className="mt-1 text-sm">{getApiErrorMessage(error, "Tente novamente em instantes.")}</p>
            </div>
          </CardContent></Card>
        )}

        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Cidade/UF</TableHead>
                    <TableHead className="w-[160px] text-right">Chamado ao criar pedido</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleClients.map((client) => {
                    const clientId = client.clientId!;
                    return (
                      <TableRow key={clientId}>
                        <TableCell className="font-medium">{client.clientFantasyName ?? client.clientCompanyName ?? "Cliente sem nome"}</TableCell>
                        <TableCell>{client.clientCodeCnx ?? "-"}</TableCell>
                        <TableCell>{client.clientCnpj ?? "-"}</TableCell>
                        <TableCell>{[client.clientCity, client.clientState].filter(Boolean).join("/") || "-"}</TableCell>
                        <TableCell className="text-right">
                          <Switch
                            checked={client.createZendeskTicketOnOrder ?? false}
                            disabled={savingIds.includes(clientId)}
                            onCheckedChange={(checked) => handleToggle(client, checked)}
                            aria-label={`Chamado no Zendesk ao criar pedido: ${client.clientFantasyName ?? client.clientCodeCnx}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleClients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                        Nenhum cliente encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {filteredClients.length > MAX_VISIBLE_ROWS && (
                <p className="border-t px-4 py-3 text-xs text-muted-foreground">
                  Exibindo {MAX_VISIBLE_ROWS} de {filteredClients.length} clientes. Refine a pesquisa para ver os demais.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
